import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslationService, GlobalMessageService, GlobalMessageType } from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { CustomUserPaymentsService } from '../../custom-user-payments-service';
import { BalanceResponse, BookingType, InvoiceHistory, InvoiceHistoryRequestData, InvoiceSortOptions, InvoiceSummaryResponse, OrderType } from '../../custom-user-payments.models';
import { getUserPayments, resetBackToUserPayments, searchUserPayments, sortUserPayments } from '../../store/custom-user-payments-actions';
import { CustomUserPaymentsState } from '../../store/custom-user-payments.reducer';
import { errorUserPayments, selectBackToOverview, selectUserPayments, selectUserPaymentsFilters } from '../../store/custom-user-payments.selectors';

@Component({
    selector: 'app-custom-user-payments',
    templateUrl: './custom-user-payments.component.html',
    styleUrls: [
        './custom-user-payments.component.scss',
        './custom-user-payments-print.component.scss'
    ],
    standalone: false
})
export class CustomUserPaymentsComponent implements OnInit {

  @ViewChild('searchForm') searchForm: NgForm;

  orderTypes = Object.values(OrderType).filter(
    (value) => typeof value === 'string'
  ) as string[];

  bookingTypes = Object.values(BookingType).filter(
    (value) => typeof value === 'string'
  ) as string[];

  invoiceOverview$: Observable<InvoiceHistory>  =
  this.store.select(selectUserPayments);
  errorOverview$: Observable<Error> = this.store.select(errorUserPayments);
  userPaymentsFilters$: Observable<InvoiceHistoryRequestData> = this.store.select(selectUserPaymentsFilters);
  backToOverview$: Observable<boolean> = this.store.select(selectBackToOverview);

  invoiceOverview: InvoiceHistory;
  invoices: InvoiceSummaryResponse[];
  balance: BalanceResponse;
  errorOverview: Error;
  userPaymentsFilters: InvoiceHistoryRequestData;
  backToOverview: boolean = false;

  bookingType: string  =
  BookingType[BookingType.ALL];
  orderType: string  =
  OrderType[OrderType.ALL];
  amountFrom: string = '';
  amountTo: string = '';
  invoiceNumber: string = '';
  referenceNumber: string = '';
  articleNumber: string = '';
  notificationNumber: string = '';
  openPositions: Boolean = true;
  todaysDate: Date = new Date();
  invoiceDateFrom: string = this.datePipe.transform(new Date(this.todaysDate).setDate(this.todaysDate.getDate() - 365), 'yyyy-MM-dd');
  invoiceDateFromMin: string;
  invoiceDateFromMax: Date = new Date();
  invoiceDateTo: string = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
  invoiceDateToMin: string;
  invoiceDateToMax: Date = new Date();

  selectedSortOption: string;
  sortOptions: string[];
  sortDirection: string = 'desc';
  compare = (v1: string | Date, v2: string | Date) =>
    v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

  compareNumbers = (v1: number , v2: number) =>
  isNaN(v1) ? -1 : isNaN(v2) ? 1 : v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

  constructor(
    private store: Store<CustomUserPaymentsState>,
    private translation: TranslationService,
    private messageService: GlobalMessageService,
    private datePipe: DatePipe,
    private customUserPaymentsService: CustomUserPaymentsService) { }

  ngOnInit(): void {

    this.backToOverview$
    .pipe(
      filter((backToOverview) => backToOverview != null),
      tap((backToOverview) => {
        this.backToOverview = backToOverview;
      })
    )
    .subscribe();

    this.userPaymentsFilters$
    .pipe(
      filter((invoiceHistoryRequestData) => invoiceHistoryRequestData != null),
      tap((invoiceHistoryRequestData) => {
        this.userPaymentsFilters = invoiceHistoryRequestData;
      })
    )
    .subscribe();

    if(this.backToOverview){
      this.usePresetFilters();
      this.store.dispatch(resetBackToUserPayments());
    }
    else{
      this.setSortOptions();
      this.getUserPayments();
    }

    this.invoiceOverview$
    .pipe(
      filter((invoiceOverview) => invoiceOverview != null),
      tap((invoiceOverview) => {
        this.invoiceOverview = invoiceOverview;
        this.invoices = [...invoiceOverview.invoices];
        this.balance = invoiceOverview.balance;
        this.onChangeSort();
      })
    )
    .subscribe();

    this.errorOverview$
    .pipe(
      filter((error) => error != null),
      tap((error) => {
        this.errorOverview = error;
      }),
      switchMap(() => this.translation.translate('customUserPayments.error')),
      tap((text) => {
        this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
      })
    )
    .subscribe();
  }

  getUserPayments(): void {
    this.invoiceOverview = null;
    this.invoices = null;
    this.balance = null;
    this.errorOverview = null;
    this.store.dispatch(getUserPayments());
  }

  setSortOptions(): void {
    this.selectedSortOption =
    InvoiceSortOptions[
      InvoiceSortOptions.invoiceDate
      ];
    this.sortOptions = this.sortOptionsAsString();
  }

  sortOptionsAsString(): string[]{
    return Object.values(
      InvoiceSortOptions
    ).filter((value) => typeof value === 'string') as string[];
  }

  onChangeSort(): void {
    this.invoices = [...this.invoiceOverview.invoices].sort((a, b) => {
      if(this.amountOrDiscount(this.selectedSortOption))
      {
       const res =this.compareNumbers(
        a[this.selectedSortOption],
        b[this.selectedSortOption]
      );
       return this.sortDirection === 'asc' ? res : -res;
      }
      else{
       const res = this.compare(
         a[this.selectedSortOption],
         b[this.selectedSortOption]
       );
       return this.sortDirection === 'asc' ? res : -res;
      }
   });
   this.store.dispatch(sortUserPayments({
    invoiceHistoryRequestData: this.getRequestData()
   }));
  }

  amountOrDiscount(selectedSortOption: string): boolean{
    return (selectedSortOption === String(InvoiceSortOptions[InvoiceSortOptions.amount]) ||
    selectedSortOption === String(InvoiceSortOptions[InvoiceSortOptions.collectedAmount]) ||
    selectedSortOption === String(InvoiceSortOptions[InvoiceSortOptions.accumulatedValue]) ||
    selectedSortOption === String(InvoiceSortOptions[InvoiceSortOptions.discount]));
  }

  changeInvoiceDateFrom(event: any) {
    this.invoiceDateFrom = event.target.value;
    if (new Date(this.invoiceDateFrom) > new Date(this.invoiceDateTo)) {
      this.invoiceDateTo = this.datePipe.transform(new Date(this.invoiceDateFrom),  'yyyy-MM-dd');
    }
  }

  changeInvoiceDateTo(event: any) {
    this.invoiceDateTo = event.target.value;
  }

  onSubmit() {
    this.invoiceDateFrom = this.searchForm.value.invoiceDateFrom;
    this.invoiceDateTo = this.searchForm.value.invoiceDateTo;
    this.amountFrom = this.searchForm.value.amountFrom;
    this.amountTo = this.searchForm.value.amountTo;
    this.invoiceNumber = this.searchForm.value.invoiceNumber;
    this.referenceNumber = this.searchForm.value.referenceNumber;
    this.articleNumber = this.searchForm.value.articleNumber;
    this.orderType = this.searchForm.value.orderType;
    this.bookingType = this.searchForm.value.bookingType;
    this.openPositions = this.searchForm.value.openPositions;
    this.searchInvoiceOverview();
  }

  searchInvoiceOverview(): void {
    this.invoiceOverview = null;
    this.invoices = null;
    this.errorOverview = null;
    this.store.dispatch(searchUserPayments({
      invoiceHistoryRequestData: this.getRequestData()
    }));
  }

  resetSearchForm(): void{
    this.sortDirection = 'desc';
    this.searchForm.reset({
      selectedBookingTypeOption:  BookingType[BookingType.ALL],
      selectedOrderTypeOption: OrderType[OrderType.ALL],
      bookingType: BookingType[BookingType.ALL],
      orderType: OrderType[OrderType.ALL],
      amountFrom: '',
      amountTo: '',
      invoiceNumber: '',
      referenceNumber: '',
      articleNumber: '',
      notificationNumber: '',
      openPositions: true,
      invoiceDateFrom: this.datePipe.transform(new Date(this.todaysDate).setDate(this.todaysDate.getDate() - 365), 'yyyy-MM-dd'),
      invoiceDateTo: this.datePipe.transform(new Date(), 'yyyy-MM-dd')
    });
    this.setSortOptions();
  }

  onExport() {
    this.customUserPaymentsService.printOrExportInvoiceHistory(this.getRequestData());
  }

  getRequestData(): InvoiceHistoryRequestData {
    var requestData = {} as InvoiceHistoryRequestData;
    requestData.dateFrom = this.datePipe.transform(this.invoiceDateFrom, 'yyyy-MM-dd').toString();
    requestData.dateTo = this.datePipe.transform(this.invoiceDateTo, 'yyyy-MM-dd').toString();
    requestData.invoiceNumber = this.invoiceNumber;
    requestData.articleNumber = this.articleNumber;
    requestData.adviceNumber = this.notificationNumber;
    requestData.onlyOpen = this.openPositions;
    requestData.referenceNumber = this.referenceNumber;
    requestData.amountFrom = this.amountFrom;
    requestData.amountTo = this.amountTo;
    requestData.orderType = this.orderType;
    requestData.bookingType = this.bookingType;
    requestData.sortOption = this.selectedSortOption;
    requestData.sortDirection = this.sortDirection;
    return requestData;
  }

  getPdf(pdfUrl: string){
    this.customUserPaymentsService.getPdf(pdfUrl);
  }

  usePresetFilters(): void{
    this.selectedSortOption = this.userPaymentsFilters.sortOption;
    this.sortDirection = this.userPaymentsFilters.sortDirection;
    this.sortOptions = this.sortOptionsAsString();
    this.bookingType = this.userPaymentsFilters.bookingType;
    this.orderType = this.userPaymentsFilters.orderType;
    this.amountFrom = this.userPaymentsFilters.amountFrom;
    this.amountTo = this.userPaymentsFilters.amountTo;
    this.invoiceNumber = this.userPaymentsFilters.invoiceNumber;
    this.referenceNumber = this.userPaymentsFilters.referenceNumber;
    this.articleNumber = this.userPaymentsFilters.articleNumber;
    this.notificationNumber = this.userPaymentsFilters.adviceNumber;
    this.openPositions = this.userPaymentsFilters.onlyOpen;
    this.invoiceDateFrom = this.datePipe.transform(this.userPaymentsFilters.dateFrom, 'yyyy-MM-dd');
    this.invoiceDateTo =  this.datePipe.transform(this.userPaymentsFilters.dateTo, 'yyyy-MM-dd');
  }

}
